import { setBy, sortBy } from '@st/utils'

import type { TSportEvent } from 'markets-store/types'
import type { Top, Rule, SbTopSportEvents } from '../../../stores/useHomeStore'

interface FormattedRule extends Rule {
  sbIds: Set<number>
  topSortOrder: number
}

function getSortedActiveRules(tops: Top[]): FormattedRule[] {
  return tops
    .filter((top) => top.active)
    .flatMap((top, topIndex) =>
      top.rules
        .filter((rule) => rule.active)
        .map((rule) => ({
          ...rule,
          topSortOrder: topIndex,
          sbIds: setBy(rule.data, ({ sbId }) => sbId),
        })),
    )
}

function validateEventByRule(event: TSportEvent, rule: FormattedRule) {
  switch (rule.sbType) {
    case 'sport':
      return rule.sbIds.has(event.sportId)
    case 'category':
      return rule.sbIds.has(event.categoryId)
    case 'tournament':
      return rule.sbIds.has(event.tournamentId)
    case 'competitor':
      return rule.fullMatchCompetitors
        ? event.competitors.every((competitor) => rule.sbIds.has(competitor.id))
        : event.competitors.some((competitor) => rule.sbIds.has(competitor.id))
    default:
      return false
  }
}

export function getTopEvents({
  events = [],
  sbTopRules = [],
  sbTopEvents,
  filter = () => true,
  quantity = 20,
}: {
  events: TSportEvent[]
  sbTopRules: Top[]
  sbTopEvents: SbTopSportEvents
  filter: (event: TSportEvent) => boolean
  quantity: number
}): TSportEvent[] {
  const topEvents: TSportEvent[] = []

  const ruleByEventId: Record<number, FormattedRule> = {}
  const rules = getSortedActiveRules(sbTopRules)

  const sbTopEventIds = new Set(
    sbTopEvents
      .filter(({ type }) =>
        ['match', 'matchOfTheDay', 'secondMatchOfTheDay'].includes(type),
      )
      .flatMap(({ sbIds }) => sbIds),
  )

  events.forEach((event) => {
    const isPassingFilters = filter(event)
    if (!isPassingFilters) return

    const isSbTopEvent = sbTopEventIds.has(event.id)
    if (isSbTopEvent) {
      topEvents.push(event)
      return
    }

    const matchingRule = rules.find((rule) => validateEventByRule(event, rule))
    if (matchingRule && !ruleByEventId[event.id]) {
      topEvents.push(event)
      ruleByEventId[event.id] = matchingRule
    }
  })

  sortBy(topEvents, (event) => [
    event.scheduled,
    sbTopEventIds.has(event.id) ? 0 : 1,
    ruleByEventId[event.id]?.topSortOrder ?? Infinity,
    ruleByEventId[event.id]?.sortOrder ?? Infinity,
    event.weight,
  ])

  return topEvents.slice(0, quantity)
}
